import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, ShieldCheck, KeyRound, Copy, Check, Fingerprint } from "lucide-react";
import { startAuthentication } from "@simplewebauthn/browser";
import { api, formatApiError } from "../lib/api";

// Paso de segundo factor (MFA obligatorio). Si el usuario no tiene MFA, lo enrola
// mostrando el QR; si ya tiene, pide el código de 6 dígitos. Al terminar entrega el
// access token final vía onSuccess(data).
export default function MfaStep({ pendingToken, enrolled, hasPasskeys, onSuccess }) {
  const [phase, setPhase] = useState(enrolled ? "verify" : "enroll");
  const [qr, setQr] = useState("");
  const [manualKey, setManualKey] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [copied, setCopied] = useState(false);

  const cfg = { headers: { Authorization: `Bearer ${pendingToken}` } };

  useEffect(() => {
    if (phase === "enroll" && !qr) {
      setLoading(true);
      api.post("/auth/mfa/totp/enroll/begin", {}, cfg)
        .then((r) => { setQr(r.data.qr_png); setManualKey(r.data.manual_key); })
        .catch((e) => setError(formatApiError(e.response?.data?.detail)))
        .finally(() => setLoading(false));
    }
  }, [phase]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const path = phase === "enroll" ? "/auth/mfa/totp/enroll/finish"
        : useRecovery ? "/auth/mfa/recovery/verify" : "/auth/mfa/totp/verify";
      const { data } = await api.post(path, { code: code.trim() }, cfg);
      if (data.recovery_codes) { setRecoveryCodes(data.recovery_codes); setPendingSuccess(data); }
      else onSuccess(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Guarda el resultado hasta que el usuario confirme haber copiado sus códigos.
  const [pendingSuccessData, setPendingSuccess] = useState(null);

  const passkeyLogin = async () => {
    setError("");
    setLoading(true);
    try {
      const origin = window.location.origin;
      const { data: options } = await api.post("/auth/passkey/login/begin", { origin }, cfg);
      const credential = await startAuthentication({ optionsJSON: options });
      const { data } = await api.post("/auth/passkey/login/finish", { credential, origin }, cfg);
      onSuccess(data);
    } catch (err) {
      if (err?.name !== "NotAllowedError")
        setError(formatApiError(err?.response?.data?.detail) || "No se pudo verificar la passkey");
    } finally {
      setLoading(false);
    }
  };

  if (recoveryCodes) {
    return (
      <div data-testid="mfa-recovery-codes">
        <div className="overline text-navy/40 mb-3">Guarda estos códigos</div>
        <h2 className="text-2xl font-medium tracking-tight mb-3">Códigos de recuperación</h2>
        <p className="text-sm text-navy/60 mb-4">Guárdalos en un lugar seguro. Cada uno sirve <b>una sola vez</b> si pierdes tu teléfono. No se volverán a mostrar.</p>
        <div className="grid grid-cols-2 gap-2 bg-white border border-navy/10 rounded-lg p-4 font-mono text-sm">
          {recoveryCodes.map((c) => <span key={c} className="text-navy">{c}</span>)}
        </div>
        <button onClick={() => { navigator.clipboard?.writeText(recoveryCodes.join("\n")); setCopied(true); }}
          className="mt-3 text-sm text-violet inline-flex items-center gap-1.5" data-testid="mfa-copy-codes">
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? "Copiados" : "Copiar códigos"}
        </button>
        <button data-testid="mfa-codes-continue" onClick={() => onSuccess(pendingSuccessData)}
          className="mt-5 w-full rounded-full bg-navy text-white py-3.5 hover:bg-navy-soft transition-colors">
          Ya los guardé, entrar a OMNIA
        </button>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="mfa-step">
      <div className="flex items-center gap-2 text-violet mb-3"><ShieldCheck className="w-5 h-5" /><span className="overline">Verificación en dos pasos</span></div>
      <h2 className="text-2xl font-medium tracking-tight mb-2">
        {phase === "enroll" ? "Configura tu autenticador" : "Ingresa tu código"}
      </h2>
      <p className="text-sm text-navy/60 mb-5">
        {phase === "enroll"
          ? "Escanea el código QR con Google Authenticator o Microsoft Authenticator y escribe el código de 6 dígitos para activarlo."
          : useRecovery ? "Escribe uno de tus códigos de recuperación."
          : "Abre tu app Authenticator y escribe el código de 6 dígitos."}
      </p>

      {phase === "enroll" && (
        <div className="mb-5 flex flex-col items-center">
          {loading && !qr ? <Loader2 className="w-6 h-6 animate-spin text-violet my-8" />
            : qr && <img src={qr} alt="Código QR MFA" className="w-44 h-44 rounded-lg border border-navy/10" data-testid="mfa-qr" />}
          {manualKey && <p className="mt-2 text-xs text-navy/40">¿No puedes escanear? Clave: <span className="font-mono">{manualKey}</span></p>}
        </div>
      )}

      <form onSubmit={submit} className="space-y-4" data-testid="mfa-form">
        <input
          data-testid="mfa-code-input"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          inputMode={useRecovery ? "text" : "numeric"}
          autoFocus
          placeholder={useRecovery ? "XXXX-XXXX" : "000000"}
          className="w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors text-center tracking-[0.3em] font-mono text-lg"
        />
        {error && <p data-testid="mfa-error" className="text-sm text-destructive">{error}</p>}
        <button data-testid="mfa-submit" type="submit" disabled={loading || !code.trim()}
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy text-white py-3.5 hover:bg-navy-soft transition-colors disabled:opacity-60">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {phase === "enroll" ? "Activar y entrar" : "Verificar y entrar"}
        </button>
      </form>

      {phase === "verify" && hasPasskeys && (
        <button onClick={passkeyLogin} disabled={loading} data-testid="mfa-passkey-btn"
          className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full border border-navy/15 py-3 text-sm text-navy/70 hover:border-violet hover:text-violet transition-colors">
          <Fingerprint className="w-4 h-4" /> Entrar con passkey (huella / Face ID)
        </button>
      )}

      {phase === "verify" && (
        <button onClick={() => { setUseRecovery(!useRecovery); setCode(""); setError(""); }}
          data-testid="mfa-toggle-recovery" className="mt-4 text-sm text-violet inline-flex items-center gap-1.5">
          <KeyRound className="w-4 h-4" /> {useRecovery ? "Usar código del autenticador" : "Usar un código de recuperación"}
        </button>
      )}
    </motion.div>
  );
}
